/**
 * TwilioParticipant Component
 *
 * Renders a video participant (local or remote) from Twilio Video.
 * Handles track subscriptions and displays video/audio status.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Participant,
  LocalParticipant,
  RemoteParticipant,
  Track,
  VideoTrack,
  AudioTrack,
  LocalVideoTrack,
  LocalAudioTrack,
  RemoteVideoTrack,
  RemoteAudioTrack,
  TrackPublication,
  RemoteTrackPublication,
} from 'twilio-video';
import { Mic, MicOff, Video, VideoOff, User, Wifi, WifiOff, Signal } from 'lucide-react';

interface TwilioParticipantProps {
  /** The Twilio participant to render */
  participant: Participant;
  /** Whether this is the local participant */
  isLocal?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether this participant is the dominant speaker */
  isDominantSpeaker?: boolean;
  /** Show network quality indicator */
  showNetworkQuality?: boolean;
  /** Custom label (overrides identity-based name) */
  label?: string;
}

/**
 * Parse participant identity to extract display name
 * Identity format: "coordinator_123_John Doe" or "member_456_Jane Smith"
 */
function parseIdentityName(identity: string): string {
  // TRUS-688 identities carry no name: "member-456" (a visit's member) and
  // "user-123" (staff in a WeaveIn meeting).
  if (/^member-\d+$/.test(identity)) return 'Member';
  if (/^user-\d+$/.test(identity)) return 'Staff';
  const parts = identity.split('_');
  if (parts.length >= 3) {
    // Join everything after the first two parts (type and id)
    return parts.slice(2).join('_').replace(/_/g, ' ');
  }
  return identity;
}

/**
 * Get participant type from identity
 */
function getParticipantType(identity: string): 'coordinator' | 'member' | 'unknown' {
  if (identity.startsWith('coordinator_')) return 'coordinator';
  if (identity.startsWith('member_') || identity.startsWith('member-')) return 'member';
  if (identity.startsWith('user-')) return 'coordinator';
  return 'unknown';
}

export default function TwilioParticipant({
  participant,
  isLocal = false,
  className = '',
  isDominantSpeaker = false,
  showNetworkQuality = true,
  label,
}: TwilioParticipantProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [videoTrack, setVideoTrack] = useState<VideoTrack | null>(null);
  const [audioTrack, setAudioTrack] = useState<AudioTrack | null>(null);
  const [networkQualityLevel, setNetworkQualityLevel] = useState<number | null>(null);

  // Derived values
  const displayName = label || (isLocal ? 'You' : parseIdentityName(participant.identity));
  const participantType = getParticipantType(participant.identity);

  /**
   * Handle track subscription/publication
   */
  const handleTrack = useCallback(
    (track: Track) => {
      if (track.kind === 'video') {
        const vTrack = track as VideoTrack;
        setVideoTrack(vTrack);
        setIsVideoEnabled(vTrack.isEnabled);

        if (videoRef.current) {
          vTrack.attach(videoRef.current);
        }
      } else if (track.kind === 'audio') {
        const aTrack = track as AudioTrack;
        setAudioTrack(aTrack);
        setIsAudioEnabled(aTrack.isEnabled);

        // Don't attach audio for local participant (prevents echo)
        if (!isLocal && audioRef.current) {
          aTrack.attach(audioRef.current);
        }
      }
    },
    [isLocal]
  );

  /**
   * Handle track unsubscription
   */
  const handleTrackRemoved = useCallback((track: Track) => {
    if (track.kind === 'video') {
      setVideoTrack(null);
      setIsVideoEnabled(false);
      (track as VideoTrack).detach().forEach((el) => el.remove());
    } else if (track.kind === 'audio') {
      setAudioTrack(null);
      setIsAudioEnabled(false);
      (track as AudioTrack).detach().forEach((el) => el.remove());
    }
  }, []);

  /**
   * Handle track enabled/disabled events
   */
  const handleTrackEnabled = useCallback((track: Track) => {
    if (track.kind === 'video') {
      setIsVideoEnabled(true);
    } else if (track.kind === 'audio') {
      setIsAudioEnabled(true);
    }
  }, []);

  const handleTrackDisabled = useCallback((track: Track) => {
    if (track.kind === 'video') {
      setIsVideoEnabled(false);
    } else if (track.kind === 'audio') {
      setIsAudioEnabled(false);
    }
  }, []);

  /**
   * Set up track subscriptions
   */
  useEffect(() => {
    // Attach existing tracks
    if (isLocal) {
      // For local participant, access tracks directly
      const localParticipant = participant as LocalParticipant;
      localParticipant.videoTracks.forEach((publication) => {
        if (publication.track) {
          handleTrack(publication.track);
        }
      });
      localParticipant.audioTracks.forEach((publication) => {
        if (publication.track) {
          handleTrack(publication.track);
        }
      });
    } else {
      // For remote participants, subscribe to tracks
      const remoteParticipant = participant as RemoteParticipant;

      // Handle existing published tracks
      remoteParticipant.tracks.forEach((publication: RemoteTrackPublication) => {
        if (publication.isSubscribed && publication.track) {
          handleTrack(publication.track);
        }
      });

      // Listen for new track subscriptions
      remoteParticipant.on('trackSubscribed', handleTrack);
      remoteParticipant.on('trackUnsubscribed', handleTrackRemoved);
    }

    // Listen for track enabled/disabled on the participant
    participant.on('trackEnabled', handleTrackEnabled);
    participant.on('trackDisabled', handleTrackDisabled);

    // Network quality for remote participants
    if (!isLocal && showNetworkQuality) {
      const remoteParticipant = participant as RemoteParticipant;
      if ('networkQualityLevel' in remoteParticipant) {
        setNetworkQualityLevel(remoteParticipant.networkQualityLevel ?? null);
      }
      remoteParticipant.on('networkQualityLevelChanged', (level: number) => {
        setNetworkQualityLevel(level);
      });
    }

    return () => {
      // Cleanup
      participant.off('trackEnabled', handleTrackEnabled);
      participant.off('trackDisabled', handleTrackDisabled);

      if (!isLocal) {
        const remoteParticipant = participant as RemoteParticipant;
        remoteParticipant.off('trackSubscribed', handleTrack);
        remoteParticipant.off('trackUnsubscribed', handleTrackRemoved);
        remoteParticipant.off('networkQualityLevelChanged', () => {});
      }

      // Detach tracks
      if (videoTrack) {
        videoTrack.detach().forEach((el) => el.remove());
      }
      if (audioTrack && !isLocal) {
        audioTrack.detach().forEach((el) => el.remove());
      }
    };
  }, [
    participant,
    isLocal,
    showNetworkQuality,
    handleTrack,
    handleTrackRemoved,
    handleTrackEnabled,
    handleTrackDisabled,
    videoTrack,
    audioTrack,
  ]);

  /**
   * Get network quality indicator color
   */
  const getNetworkQualityColor = (level: number | null): string => {
    if (level === null) return 'text-gray-400';
    if (level >= 4) return 'text-green-500';
    if (level >= 3) return 'text-yellow-500';
    if (level >= 2) return 'text-orange-500';
    return 'text-red-500';
  };

  /**
   * Get network quality bars (1-5)
   */
  const getNetworkQualityBars = (level: number | null): JSX.Element => {
    const bars = [1, 2, 3, 4, 5];
    return (
      <div className="flex items-end gap-0.5 h-3">
        {bars.map((bar) => (
          <div
            key={bar}
            className={`w-1 rounded-sm transition-colors ${
              level !== null && level >= bar
                ? bar <= 2
                  ? 'bg-red-500'
                  : bar <= 3
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
                : 'bg-gray-600'
            }`}
            style={{ height: `${bar * 20}%` }}
          />
        ))}
      </div>
    );
  };

  return (
    <div
      className={`relative bg-gray-900 rounded-lg overflow-hidden ${
        isDominantSpeaker ? 'ring-2 ring-teal-500' : ''
      } ${className}`}
    >
      {/* Video element */}
      <video
        ref={videoRef}
        autoPlay
        muted={isLocal} // Always mute local to prevent echo
        playsInline
        className={`w-full h-full object-cover ${!isVideoEnabled ? 'hidden' : ''}`}
      />

      {/* Audio element (for remote participants only) */}
      {!isLocal && <audio ref={audioRef} autoPlay />}

      {/* Video off placeholder */}
      {!isVideoEnabled && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
          <div
            className={`w-20 h-20 rounded-full flex items-center justify-center ${
              participantType === 'coordinator'
                ? 'bg-teal-700'
                : participantType === 'member'
                ? 'bg-purple-700'
                : 'bg-gray-700'
            }`}
          >
            <User className="w-10 h-10 text-white" />
          </div>
        </div>
      )}

      {/* Participant info overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
        <div className="flex items-center justify-between">
          {/* Name and type badge */}
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-black/50 rounded text-white text-sm font-medium">
              {displayName}
            </span>
            {participantType !== 'unknown' && (
              <span
                className={`px-1.5 py-0.5 rounded text-xs ${
                  participantType === 'coordinator'
                    ? 'bg-teal-600 text-white'
                    : 'bg-purple-600 text-white'
                }`}
              >
                {participantType === 'coordinator' ? 'Staff' : 'Member'}
              </span>
            )}
          </div>

          {/* Status indicators */}
          <div className="flex items-center gap-2">
            {/* Network quality */}
            {showNetworkQuality && networkQualityLevel !== null && (
              <div className="flex items-center gap-1 px-1.5 py-0.5 bg-black/50 rounded">
                {getNetworkQualityBars(networkQualityLevel)}
              </div>
            )}

            {/* Audio status */}
            {!isAudioEnabled && (
              <span className="p-1 bg-red-500 rounded">
                <MicOff className="w-3 h-3 text-white" />
              </span>
            )}

            {/* Video status */}
            {!isVideoEnabled && (
              <span className="p-1 bg-red-500 rounded">
                <VideoOff className="w-3 h-3 text-white" />
              </span>
            )}

            {/* Dominant speaker indicator */}
            {isDominantSpeaker && (
              <span className="p-1 bg-teal-500 rounded animate-pulse">
                <Mic className="w-3 h-3 text-white" />
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Connecting indicator (shown while no video) */}
      {!videoTrack && !audioTrack && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center mx-auto mb-2">
              <User className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-400 text-sm">Connecting...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export { TwilioParticipant };
